import {TransitionOptions, useTransition} from '@logistics-frontend/blocks'
import {ServerError} from '@logistics-frontend/core/utils/network'
import {noop} from '@logistics-frontend/utils'
import {useCallback, useMemo} from 'react'
import {useLocation} from 'react-router-dom'

import {fetchTariffZoneName} from '../../api'
import {TariffZonesItem} from '../../types'
import {sortArrayBy} from '../../utils'

export const getZonesByCountry = (countryCode: string, zones: TariffZonesItem[] = []) =>
  sortArrayBy<TariffZonesItem>('translation')(zones.filter(({country}) => country === countryCode))

type Props = TransitionOptions<string | undefined, ServerError> & {
  tariffZones: TariffZonesItem[]
  countryCode: string
  defaultZone?: string
  zone?: string
}

type UpdateZoneName = Pick<Props, 'countryCode' | 'onCommit' | 'onError'>

export const useTariffZoneName = ({zone, tariffZones, countryCode, defaultZone, onCommit = noop}: Props) => {
  const search = new URLSearchParams(useLocation().search).get('s') ?? ''

  const zonesByCountry = useMemo(() => getZonesByCountry(countryCode, tariffZones), [tariffZones, countryCode])
  const searchResult = useMemo(
    () => zonesByCountry.filter(({translation}) => translation?.toLowerCase().indexOf(search.toLowerCase()) !== -1),
    [zonesByCountry, search],
  )
  const searchedZone = searchResult?.[0]?.name

  const [{payload, isPending}, startTransition] = useTransition<string | undefined, ServerError>(
    async () => {
      const zoneName = Boolean(zone || search)
        ? zone || searchedZone
        : await fetchTariffZoneName(zonesByCountry, countryCode, defaultZone, searchedZone)

      onCommit(zoneName)
      return zoneName
    },
    {key: 'fetch/tariff/zone/name'},
  )

  const updateZoneName = useCallback(
    ({countryCode, ...options}: UpdateZoneName) => {
      const zonesByCountry = getZonesByCountry(countryCode, tariffZones)
      const fallbackZone = zonesByCountry[0]?.name

      startTransition(() => fetchTariffZoneName(zonesByCountry, countryCode, defaultZone, fallbackZone), options)
    },
    [defaultZone, tariffZones],
  )

  return {zoneName: payload, searchResult, updateZoneName, isPending}
}
